import {getPATDay} from '@account/utils/dates';

// Ledger operation types — mirror the backend LedgerOperationSchema.operation_type.
export const LedgerOperationTypes = {
  PROVISION: 'PROVISION',
  ALLOCATE: 'ALLOCATE',
  RECALL: 'RECALL',
  ADJUSTMENT: 'ADJUSTMENT',
  CANCELLATION: 'CANCELLATION',
  REVERSAL: 'REVERSAL',
};

// Normalize a term/operation date to a UTC start-of-day millisecond stamp so
// boundaries compare on the calendar day only (the same normalization the term
// list uses for display). Returns null for missing/invalid input.
const dayMs = date => {
  const d = getPATDay(date);
  return d?.isValid ? d.toMillis() : null;
};

// deriveExpiredTermLabel explains a term the backend flagged "expired" purely
// because its end_date < now. When allocating a product "from today forward",
// the backend splits one entitlement window into a short past-dated fragment
// (full quota) plus the allocated window (drained to 0). The fragment shows up
// as a bare "Expired" chip even though nothing lapsed — it's an artifact of the
// split.
//
// The glue rule is deterministic, not a heuristic: a term boundary that
// coincides with a ledger operation's valid_from/valid_to on the same PA was
// created by that operation. Given the term and the grouped ledger ops, return
// the operation-derived label, or null to keep the plain "Expired" chip
// (genuine expiry, or no ledger data — e.g. PAs provisioned before the ledger
// existed).
export const deriveExpiredTermLabel = (term, ledgerOps = []) => {
  if (!term || !Array.isArray(ledgerOps) || ledgerOps.length === 0) return null;

  const termStart = dayMs(term.start_date);
  const termEnd = dayMs(term.end_date);
  if (termStart === null || termEnd === null) return null;

  const windowMatches = op =>
    dayMs(op.valid_from) === termStart && dayMs(op.valid_to) === termEnd;

  // 1. Exact ALLOCATE window — this fragment was allocated out to a workspace.
  const allocate = ledgerOps.find(
    o => o.operation_type === LedgerOperationTypes.ALLOCATE && windowMatches(o)
  );
  if (allocate) {
    const ws = allocate.destination_workspace_name;
    return {
      status: 'Allocated',
      detail: ws ? `→ ${ws}` : '→ workspace',
      workspaceId: allocate.destination_workspace_id ?? null,
      tooltip: `This entitlement window was allocated to ${
        ws || 'a workspace'
      }. It isn't a lapsed entitlement.`,
    };
  }

  // 2. Exact RECALL window — quota was recalled from a workspace.
  const recall = ledgerOps.find(
    o => o.operation_type === LedgerOperationTypes.RECALL && windowMatches(o)
  );
  if (recall) {
    return {
      status: 'Recalled',
      detail: null,
      workspaceId: null,
      tooltip:
        "Quota for this window was recalled from a workspace. It isn't a lapsed entitlement.",
    };
  }

  // 3. Holding slice — the pre-allocation fragment left behind when a product
  //    is allocated "from today forward". The tell is deterministic: an ALLOCATE
  //    op starts the day after this fragment ends (the contiguous split that
  //    created it), and the fragment sits inside the PROVISION window. We require
  //    the adjacent ALLOCATE (not merely "sub-window of provision") so a
  //    naturally-lapsed earlier term of a multi-year provision stays "Expired".
  const provision = ledgerOps.find(
    o => o.operation_type === LedgerOperationTypes.PROVISION
  );
  if (provision) {
    const provStart = dayMs(provision.valid_from);
    const provEnd = dayMs(provision.valid_to);
    const withinProvision =
      provStart !== null &&
      provEnd !== null &&
      termStart >= provStart &&
      termEnd <= provEnd;

    const dayAfterTermEnd = getPATDay(term.end_date)
      ?.plus({days: 1})
      .toMillis();
    const hasAdjacentAllocate = ledgerOps.some(
      o =>
        o.operation_type === LedgerOperationTypes.ALLOCATE &&
        dayMs(o.valid_from) === dayAfterTermEnd
    );

    if (withinProvision && hasAdjacentAllocate) {
      return {
        status: 'Provisioned',
        detail: '(unallocated)',
        workspaceId: null,
        tooltip:
          "This window held quota before it was allocated to a workspace. It isn't a lapsed entitlement.",
      };
    }
  }

  // 4. Nothing matched — genuine expiry; keep the plain "Expired" chip.
  return null;
};
